import {formatDate, formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {adbPct} from '@features/admin-panel/helpers/admin-dashboard-format';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbDrillRowView} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {AdminDashboardDrillView} from '@features/admin-panel/models/admin-dashboard-drill.models';
import {AdminDashboardDrillService} from '../drill/admin-dashboard-drill.service';
import {
  AdminDashboardReportBlock,
  AdminDashboardReportProvider,
  AdminDashboardReportRow
} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardQueryParams, AdminDashboardWidgetState} from '@features/admin-panel/models/admin-dashboard.models';
import {
  AdminDashboardEntityHeadroom,
  AdminDashboardEntityHeadroomDrill,
  AdminDashboardEntityHeadroomRow,
  AdminDashboardHeadroomBand
} from '@features/admin-panel/models/admin-dashboard-headroom.models';

/** One of the card's three counters: its band, its label and how many clients are in it. */
export interface AdminDashboardHeadroomCounter {
  band: AdminDashboardHeadroomBand;
  label: string;
  count: number;
}

/** The card's title and description, on screen and in the PDF report. */
const TITLE = 'Entity licence headroom';
const DESCRIPTION = 'Clients against their licensed entities.';
/** The PDF row stripe of each band. */
const REPORT_TONES: {[band: string]: 'red' | 'amber' | 'green'} = {over: 'red', near: 'amber', ok: 'green'};
/** The drill's title of each band (ITP-E-215-04). */
const DRILL_TITLES: {[band: string]: string} = {
  over: 'Over their entity licence',
  near: 'Near their entity licence',
  ok: 'Within their entity licence'
};
/** The end of the drill's subtitle of each band: "{N clients} over their licensed entities." */
const DRILL_SUBTITLES: {[band: string]: string} = {
  over: 'over their licensed entities',
  near: 'at 90% or more of their licensed entities',
  ok: 'under 90% of their licensed entities'
};
/** The Use pill's tone of each band. */
const USE_TONES: {[band: string]: 'red' | 'amber' | 'green'} = {over: 'red', near: 'amber', ok: 'green'};

/**
 * Projects, scenarios & entities "Entity licence headroom" (ITP-E-215-03, Plan ITP-P-50 Q7/Q16): how many clients use
 * more than their licensed entities, 90 % or more, or less; the top 5 by use with "{entities} of {licensed} entities ·
 * {pct}" and the projected day each reaches its limit. Only clients with a licensed entity count in Client Manager
 * count, so while none has one (every cluster today) the card reports empty and the dashboard hides it; on
 * Non-clients the server answers not applicable and the card is hidden too. Reloads whenever the params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-entity-headroom',
  templateUrl: './admin-dashboard-entity-headroom.component.html',
  styleUrls: ['./admin-dashboard-entity-headroom.component.scss']
})
export class AdminDashboardEntityHeadroomComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  @Input() params: AdminDashboardQueryParams;
  /** Evidence was asked for a company in a counter's drill: the dashboard opens its why panel (ITP-E-215-04). */
  @Output() companySelected = new EventEmitter<string>();
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly title = TITLE;
  readonly description = DESCRIPTION;
  /** The states' texts, shared by the template and the PDF report. */
  readonly notApplicableText = 'No Client Manager record';
  readonly emptyText = 'No client has a licensed entity count in this view';
  readonly failedTitle = 'Couldn\'t load entity licence headroom';

  data: AdminDashboardEntityHeadroom | null = null;
  /** Over / Near limit / Within licence, set with each response. */
  counters: AdminDashboardHeadroomCounter[] = [];
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>, private drill: AdminDashboardDrillService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('platform-usage', this.host.nativeElement, this);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.reportRegistry.unregister(this);
  }

  /** Applicable, and at least one client has a licensed entity count. */
  get hasContent(): boolean {
    const data = this.data;
    return !!data && !data.notApplicable && (data.over ?? 0) + (data.near ?? 0) + (data.ok ?? 0) > 0;
  }

  /** Loaded, applicable, and no client with a licensed entity count. */
  get isEmpty(): boolean {
    return this.loaded && !this.failed && !this.hasContent && !this.data?.notApplicable;
  }

  /** The top list as the server sent it (at most 5). */
  get top(): AdminDashboardEntityHeadroomRow[] {
    return this.data?.top ?? [];
  }

  /** "1 over · 2 near limit · 14 within licence" */
  get summaryText(): string {
    return `${this.int(this.data?.over)} over · ${this.int(this.data?.near)} near limit · ${this.int(this.data?.ok)} within licence`;
  }

  /** "1,234": en-US grouping, a missing number counts as 0. */
  int(value: number | null | undefined): string {
    return formatNumber(value ?? 0, 'en-US', '1.0-0');
  }

  /** "1,200 of 2,000 entities · 60%" */
  useText(row: AdminDashboardEntityHeadroomRow): string {
    return `${this.int(row?.entities)} of ${this.int(row?.licensedEntities)} entities · ${adbPct(row?.utilisation ?? 0)}`;
  }

  /** "reaches limit ~13 Nov 2026", or '' when the server projected no day. */
  reachesText(row: AdminDashboardEntityHeadroomRow): string {
    return row?.reachesLimitOn ? `reaches limit ~${formatDate(row.reachesLimitOn, 'd MMM yyyy', 'en-US', 'UTC')}` : '';
  }

  /**
   * A counter was clicked (Plan ITP-P-50 story 14): the drill lists that band's clients with their entities, licensed
   * entities, use and projected limit day. A counter at 0 opens nothing. Evidence there emits companySelected.
   */
  openBand(band: AdminDashboardHeadroomBand): void {
    const counter = this.counters.find(c => c.band === band);
    if (!counter?.count || !this.params) {
      return;
    }
    const params = this.params;
    this.drill.open({
      title: DRILL_TITLES[band],
      columns: [
        {header: 'Entities', align: 'end'},
        {header: 'Licensed', align: 'end'},
        {header: 'Use', align: 'end'},
        {header: 'Reaches limit', align: 'end'}
      ],
      load: () => this.dataService.getWithExtra<AdminDashboardEntityHeadroomDrill>(
        'AdminDashboardEntityHeadroomDrillQuery', params, {Band: band}
      ).pipe(map(response => this.drillView(band, response)))
    }).subscribe(result => {
      if (result?.action === 'evidence') {
        this.companySelected.emit(result.companyId);
      }
    });
  }

  /** The drill's view of a response: "2 clients at 90% or more of their licensed entities.", one row per client. */
  drillView(band: AdminDashboardHeadroomBand, response: AdminDashboardEntityHeadroomDrill | null | undefined): AdminDashboardDrillView {
    const rows = Array.isArray(response?.Rows) ? response.Rows : [];
    const total = response?.Total ?? rows.length;
    return {
      subtitle: `${countOf(total, 'client')} ${DRILL_SUBTITLES[band]}.`,
      rows: rows.map(row => adbDrillRowView(row, [
        {text: this.int(row.entities)},
        {text: this.int(row.licensedEntities)},
        {text: adbPct(row.utilisation ?? 0), tone: USE_TONES[band], pill: true},
        {text: row.reachesLimitOn ? formatDate(row.reachesLimitOn, 'd MMM yyyy', 'en-US', 'UTC') : '–'}
      ])),
      total
    };
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: TITLE, text: this.failedTitle}];
    }
    if (this.data?.notApplicable) {
      return [{kind: 'empty', title: TITLE, text: this.notApplicableText}];
    }
    if (!this.hasContent) {
      return [{kind: 'empty', title: TITLE, text: this.emptyText}];
    }
    return [{
      kind: 'table',
      title: TITLE,
      subtitle: `${DESCRIPTION} ${this.summaryText}`,
      columns: [
        {header: 'Company', weight: 3},
        {header: 'Entities', align: 'right'},
        {header: 'Licensed', align: 'right'},
        {header: 'Use', align: 'right'},
        {header: 'Reaches limit', align: 'right', weight: 2}
      ],
      rows: this.top.map((row): AdminDashboardReportRow => ({
        cells: [
          row.companyName || row.companyId,
          this.int(row.entities),
          this.int(row.licensedEntities),
          adbPct(row.utilisation ?? 0),
          row.reachesLimitOn ? formatDate(row.reachesLimitOn, 'd MMM yyyy', 'en-US', 'UTC') : '–'
        ],
        tone: REPORT_TONES[row.band]
      }))
    }];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardEntityHeadroom>(
      'AdminDashboardEntityHeadroomQuery', this.params, 'Headroom'
    ).subscribe({
      next: data => this.show(data ?? null, false),
      error: () => this.show(null, true)
    });
  }

  /** Not applicable and no client with a licensed entity count have nothing to show: both report empty. */
  private show(data: AdminDashboardEntityHeadroom | null, failed: boolean): void {
    this.data = data;
    this.counters = [
      {band: 'over', label: 'Over', count: data?.over ?? 0},
      {band: 'near', label: 'Near limit', count: data?.near ?? 0},
      {band: 'ok', label: 'Within licence', count: data?.ok ?? 0}
    ];
    this.failed = failed;
    this.loaded = true;
    this.report(failed ? 'failed' : this.hasContent ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }
}
